import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { CommonModule } from '@angular/common';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { ParticipantLeadInfo } from '../../../../shared/interfaces/participant-lead-info';
import { LeadDetailsService } from '../../../../services/lead-details.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { AuthorizeFieldDirective } from '../../../../shared/authorization/authorizeField.directive';
import { AuthorizeFieldViewDirective } from '../../../../shared/authorization/authorizeFieldView.directive';
import { ADDITIONAL_ATTRIBUTE_MAP, ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS, RADIO_YES_NO_OPTIONS, STATIC_DATA } from '../../../../shared/constants/static-data.constants';
import { ButtonComponent } from '@ppl/ui';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { NotificationService } from '../../../../shared/services/notification.service';
import { SectionsService } from '../../../../services/sections.service';
import { DatepickerComponent } from '@ppl/ui';
import { DropdownSelectComponent } from '@ppl/ui';
import { ClaimsService } from '../../../../services/claims.service';
import { EnrollmentDataService } from 'src/app/services/enrollment-data.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'ppl-lead-detail-participant-info',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    CommonModule,
    NgZorroModule,
    AuthorizeDirective,
    AuthorizeFieldDirective,
    AuthorizeFieldViewDirective,
    ButtonComponent,
    DatepickerComponent,
    DropdownSelectComponent,
  ],
  templateUrl: './lead-detail-participant-info.component.html',
  styleUrl: './lead-detail-participant-info.component.css',
})
export class LeadDetailParticipantInfoComponent
  extends BaseComponent
  implements OnInit, OnChanges {
  mode = 'read';
  filledInfo!: ParticipantLeadInfo;
  infoForm!: FormGroup;
  contactMethods: any;
  programs: LookupValueItem[] = [];
  statesList: any;
  regionalCenterList: any;
  genderList: any;
  conservatorshipTypes: any[] = [];
  prefList: any;
  savedData!: ParticipantLeadInfo;
  steps: any;
  approvedStatus: string = STATIC_DATA.APPROVED;
  leadStatus = '';
  contactStsReasonLkpList: any;
  waiver: any;
  fiEntries: any[] = [];
  mcoEntries: any[] = [];
  ldssOfficeValues: LookupValueItem[] = [];
  currentFIValues: LookupValueItem[] = [];

  sectionName = '';
  participantRegisterMode: any[] = [];

  @Input() set sectionData(data: any) {
    this.steps = data
  }

  @Input() enrollmentType !: string;

  @Input() entry !: string;
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() programCode !: string;
  @Input() selectedLead = 0;
  @Input() readOnly = false;
  radioOptions = RADIO_YES_NO_OPTIONS;
  relationshipOptions: any;
  eyeColors: any;
  hairColors: any;
  program: any;
  raceOptions: any;
  taxExemptOptions: any;
  @Output() leadUpdated = new EventEmitter<boolean>();
  isExternalEnvironment: boolean = false;
  nprftDocumentationReceivedOptions: any[] = [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }];
  recieveGoodsAndServicesList: any = [
    { lkpValueCode: 1, lkpValueName: 'Yes' },
    { lkpValueCode: 2, lkpValueName: 'No' }
  ];

  constructor(
    private fb: FormBuilder,
    private leadDetailsService: LeadDetailsService,
    private userService: UserService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private notificationService: NotificationService,
    private sectionService: SectionsService,
    private claimsService: ClaimsService,
    private enrollmentDataService: EnrollmentDataService,
  ) {
    super();
  }

  /** True when at least one Conservator (CNV) on this participant is also a provider for them
   *  (IR / IC / VE / IF). When true, the conservatorship type must be "Limited" — "General"
   *  is removed from the dropdown options. */
  restrictConservatorshipToLimited = false;

  get availableConservatorshipTypes(): any[] {
    if (!this.restrictConservatorshipToLimited) return this.conservatorshipTypes;
    return this.conservatorshipTypes.filter(t => !this.isGeneralConservatorshipOption(t));
  }

  private isGeneralConservatorshipOption(opt: any): boolean {
    return String(opt?.lkpValueName ?? '').trim().toLowerCase().startsWith('general');
  }

  private isGeneralConservatorshipCode(code: any): boolean {
    if (code == null || code === '') return false;
    const match = this.conservatorshipTypes.find(t => t?.lkpValueCode === code);
    return !!match && this.isGeneralConservatorshipOption(match);
  }

  private evaluateConservatorshipRestriction(): void {
    // Only applicable on the provider details view. Participant-side entries never need this gate.
    const isProviderEntry = this.entry === STATIC_DATA.PROVIDER || this.entry === STATIC_DATA.PROVIDER_ENROLLMENT;
    if (!isProviderEntry || !this.programCode || !this.selectedLead) {
      this.restrictConservatorshipToLimited = false;
      return;
    }

    // Enrollment-type gate: only run the API check when this provider's enrollment type set
    // includes 'conservator' (CNV). Without that, there's no conservatorship dropdown decision
    // to make — skip the network call entirely.
    const enrollmentTypes: string[] = this.sectionService.getEnrollmentType() ?? [];
    const hasCnvEnrollmentType = enrollmentTypes.some(t => {
      const ui = String(t ?? '').toLowerCase();
      return ui === 'conservator' || ui === 'cnv';
    });
    if (!hasCnvEnrollmentType) {
      this.restrictConservatorshipToLimited = false;
      return;
    }

    // Hit GET_PROVIDER_ENROLLMENT_INFO (api/enrollment/provider/v1/getProviderEnrollmentData/{programCode}/{providerId}).
    // Response carries `participantAssociations` as a flat array of {participantId, providerId,
    // providerType, isActive}. Restriction applies when this provider has an active CNV role for
    // a participant AND is also an active IR/IC/VE/IF for the same participant.
    this.enrollmentDataService
      .getAssociationsDetails(this.programCode, this.selectedLead, false)
      .subscribe((response: any) => {
        const associations: any[] = response?.responsedata?.participantAssociations ?? [];
        // Quick exit: provider has no active CNV enrollment → nothing to gate.
        const hasActiveCnv = associations.some((a: any) => a?.isActive && a.providerType === 'CNV');
        if (!hasActiveCnv) {
          this.restrictConservatorshipToLimited = false;
          return;
        }
        const providerCodes = new Set(['IR', 'IC', 'VE', 'IF']);
        const cnvParticipantIds = new Set<any>();
        const providerParticipantIds = new Set<any>();
        for (const a of associations) {
          if (!a?.isActive) continue;
          if (a.providerType === 'CNV') cnvParticipantIds.add(a.participantId);
          else if (providerCodes.has(a.providerType)) providerParticipantIds.add(a.participantId);
        }
        // Overlap = any participant where this provider is BOTH a CNV and a provider.
        let overlap = false;
        for (const pid of cnvParticipantIds) {
          if (providerParticipantIds.has(pid)) { overlap = true; break; }
        }
        this.restrictConservatorshipToLimited = overlap;
      });
  }


  ngOnInit(): void {
    this.isExternalEnvironment = environment.isExternal;
    this.createForm();
    this.getLookupValues();
    this.program = this.userService.getSelectedProgram();

    this.leadDetailsService.getLeadStatus().subscribe((status: string) => {
      this.leadStatus = status;
    });

    if (this.mode === 'read') {
      this.leadDetailsService.getUpdatedLeadData().subscribe(
        (data) => {
          if (data?.responsedata?.infoData) {
            this.filledInfo = data?.responsedata?.infoData;
            this.filledInfo.registerPref = data?.responsedata?.communicationPreferences?.registerPref;
            if (data.responsedata.infoData.additionalAttributes?.length > 0) {
              const additionalAttributes = data.responsedata.infoData.additionalAttributes;
              const recieveGoodsAndServicesAttribute = additionalAttributes.filter((data: any) => data.additionalAttributesId === ADDITIONAL_ATTRIBUTE_MAP.recieveGoodsAndServices);
              if (recieveGoodsAndServicesAttribute?.length > 0) {
                const mostRecentRecieveGoodsAndServices = recieveGoodsAndServicesAttribute.reduce((latest: any, current: any) => {
                  return this.parseDate(current.modifiedDate) > this.parseDate(latest.modifiedDate) ? current : latest;
                });
                this.filledInfo.recieveGoodsAndServices = mostRecentRecieveGoodsAndServices?.value ?? undefined;
              } else {
                this.filledInfo.recieveGoodsAndServices = undefined;
              }
            }
            this.savedData = JSON.parse(JSON.stringify(this.filledInfo));
            // this.savedData.registerPref = data?.responsedata?.communicationPreferences?.registerPref;
          } else {
            this.filledInfo = {};
            this.savedData = null as any;
          }

          if (this.savedData && this.savedData.program) {
            this.claimsService.getServiceData(this.savedData.program).subscribe((data: any) => {
              this.regionalCenterList = data?.responsedata?.organizations.map((data: any) => {
                return { lkpValueCode: data.orgId, lkpValueName: data.orgName };
              });
            });
          }
        }
      )
    }

    // This api is getting the organizations information in programs other than lead / interest program.
    this.lookupService.getOrgDataByType(this.program?.program_code ?? '',).subscribe((data: any) => {
      if (data.status === 200) {
        // replacing fientries and mcoentries filter condition as per new org type lookups to support CA SDP program.
        this.fiEntries = data.fiEntries;
        this.mcoEntries = data.mcoEntries;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {

      if (changes['sectionData'] && changes['sectionData'].currentValue) {
        this.secData = this.steps['participant_info'];
      }
    } catch (e) {
      console.log('Error in getting section data and updating latest info', e);
    }

  }

  createForm(): void {
    this.infoForm = this.fb.group({
      businessName: [''],
      legalName: [''],
      vendorName: [''],
      dbaName: [''],
      fname: [''],
      mname: [''],
      lname: [''],
      maiden_name: [''],
      dob: [null],
      eligiblityStartDate: [null],
      eligiblityEndDate: [null],
      gender: [null],
      conservatorship: [null],
      participant_id: [''],
      contactStsReasonLkp: [null],
      contactStsComments: [''],
      leadId: [],
      state: [''],
      regionalCenter: [''],
      waiver: [null],
      ldssOffice: [],
      currentFI: [],
      mcoOrgName: [null],
      fiOrgName: [null],
      newfiOrgName: [''],
      isShowOnDirectory: [],
      isProvidingServices: [null],
      relationToParticipant: [],
      additionalDemographics: [],
      race: [],
      heightFeet: [],
      heightInches: [],
      weight: [],
      eyeColor: [],
      hairColor: [],
      birthPlace: [],
      birthCity: [],
      birthState: [],
      birthCountry: [],
      registerPref: [],
      taxExempt: [],
      nprftDocumentationReceived: [],
      yearsInState: [],
      referralId: [],
      recieveGoodsAndServices: [''],
      servicesAtBusinessLocation: [null],
      servingIndividualsWithDisabilities: [null]
    });
  }

  getLookupValues(): void {
    this.statesList = this.lookupService.getLookupValue('STATE');
    this.programs = this.getLookup('program_code');
    this.genderList = this.lookupService.getLookupValue('Gender');
    this.prefList = this.lookupService.getLookupValue('Participant_Register_Mode');
    this.waiver = this.getLookup('Waiver');
    this.participantRegisterMode = this.getLookup('Participant_Register_Mode');
    this.relationshipOptions = this.getLookup('Relationship');
    this.eyeColors = this.getLookup('Eye Color');
    this.hairColors = this.getLookup('Hair Color');
    this.raceOptions = this.getLookup('Race');
    this.taxExemptOptions = this.getLookup('Tax Exemption');
    this.conservatorshipTypes = this.getLookup('Conservatorship_Type');
    this.evaluateConservatorshipRestriction();
    // Re-evaluate whenever an association is added / removed / status-changed (e.g. a CNV being
    // assigned or disassociated). setParticipantAssocChanged is broadcast across the app on
    // every association mutation, so we don't need a CNV-specific signal.
    this.enrollmentDataService.getParticipantAssocChanged().subscribe(() => {
      this.evaluateConservatorshipRestriction();
    });
    this.ldssOfficeValues = this.getLookup('LDSS_Office');
    this.currentFIValues = this.getLookup('CFI');
    this.contactStsReasonLkpList = this.getLookup('contact_status_reason_lkp').filter(
      (item: any) => !['RTCD', 'TPC'].includes(item.lkpValueCode)
    );
  }

  editPersonalInfo() {
    this.infoForm.controls['leadId'].setValue(this.filledInfo.leadId);


    if (this.filledInfo) {
      this.infoForm.patchValue(this.filledInfo);
      if (this.filledInfo.fiOrgName) {
        this.setCurrentFMSValues();
      }

      const updateObj: any = {
        'dob': this.filledInfo.dob ? this.dateTimeFormatterService.formatStringToDate(this.filledInfo.dob as string) : '',
        'isShowOnDirectory': this.filledInfo?.isShowOnDirectory === true ? 'Yes' : 'No',
        'isProvidingServices': this.filledInfo?.isProvidingServices === true ? 'Yes'
          : this.filledInfo?.isProvidingServices === false ? 'No'
            : this.filledInfo?.isProvidingServices
      };
      if (this.filledInfo?.eligiblityStartDate) {
        updateObj['eligiblityStartDate'] = this.dateTimeFormatterService.formatStringToDate(this.filledInfo.eligiblityStartDate as string);
      }
      if (this.filledInfo?.eligiblityEndDate) {
        updateObj['eligiblityEndDate'] = this.dateTimeFormatterService.formatStringToDate(this.filledInfo.eligiblityEndDate as string);
      }
      this.infoForm.patchValue(updateObj);
    }

    this.mode = 'edit';
  }

  setCurrentFMSValues() {
    const currentFMSName = this.filledInfo.fiOrgName;
    const currentFMSObj = this.fiEntries.find((item: any) => item.orgName === currentFMSName);
    if (currentFMSObj == null || currentFMSObj == undefined) {
      this.filledInfo.fiOrgName = 'Other';
      this.infoForm.controls['fiOrgName'].setValue('Other');
      this.infoForm.controls['newfiOrgName'].setValue(currentFMSName);
    }
  }

  getadditionalAttributes(values: any, attributeMap: any) {
    return Object.entries(attributeMap)
      .filter(([key]) => values[key] !== undefined && values[key] !== null)
      .map(([key, attributeId]) => ({
        additionalAttributesId: attributeId,
        value: String(values[key])
      }));
  }

  cancelEdit() {
    this.mode = 'read';
    this.validationErrors = {};
    this.infoForm.reset();
  }

  save() {
    let formaData = { ...this.infoForm.value };

    formaData.dob = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.dob as Date);
    formaData.additionalAttributes = this.getadditionalAttributes(this.infoForm.value, ADDITIONAL_ATTRIBUTE_MAP);
    if (this.infoForm.value?.eligiblityStartDate) {
      formaData.eligiblityStartDate = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.eligiblityStartDate as Date);
    }
    if (this.infoForm.value?.eligiblityEndDate) {
      formaData.eligiblityEndDate = this.dateTimeFormatterService.formatDateToString(this.infoForm.value.eligiblityEndDate as Date);
    }
    try {
      this.validationErrors = performValidation(this.infoForm.value, this.secData, 'Next', undefined, undefined, this.sectionService.getFlow(), this.sectionService.getEnrollmentType());
    } catch {
      console.log("Error in validations");
    }

    // Save-time guard: a conservator who is also a provider for this participant cannot have
    // conservatorship type "General" — must be "Limited". Mirrors the dropdown filter so the
    // rule still holds if the form was populated by some path that bypassed the UI restriction.
    if (this.restrictConservatorshipToLimited && this.isGeneralConservatorshipCode(formaData?.conservatorship)) {
      this.validationErrors = {
        ...this.validationErrors,
        conservatorship: 'Conservatorship type must be "Limited" because the conservator is also a provider for this participant.'
      };
    }

    // save data to server if no validation errors
    if (Object.keys(this.validationErrors).length === 0) {
      let mcoOrgId = null;
      let fiOrgId = null;
      if (this.infoForm.value.mcoOrgName) {
        mcoOrgId = this.mcoEntries.find((entry: any) => entry.orgName === this.infoForm.value.mcoOrgName)?.orgId;
      }
      if (this.infoForm.value.fiOrgName) {
        fiOrgId = this.fiEntries.find((entry: any) => entry.orgName === this.infoForm.value.fiOrgName)?.orgId || null;
        if (this.infoForm.value.fiOrgName === 'Other') {
          // setting the value of fiOrgName to newfiOrgName input value when Other is selected.
          formaData.fiOrgName = this.infoForm.value.newfiOrgName;
        }
      }
      formaData = {
        ...formaData,
        fname: formaData.fname.trim(),
        lname: formaData.lname.trim(),
        mcoOrgId,
        fiOrgId,
        modifiedDate: this.filledInfo.modifiedDate
      };

      const payload = this.preparePayload(formaData);
      // save data to server 
      this.leadDetailsService.updateLeadDemographics(payload, this.entry).subscribe({
        next: () => {
          this.leadUpdated.emit(true);
          this.cancelEdit();
        },
        error: () => {
          this.leadUpdated.emit(true);
          this.cancelEdit();
        }
      });
    }
  }

  preparePayload(source: any) {
    const isShowOnDirectory = source.isShowOnDirectory === 'Yes' ? true : false;
    const isProvidingServices = source.isProvidingServices === 'Yes' ? true : source.isProvidingServices === 'No' ? false : source.isProvidingServices;

    let payload: any = {};
    payload = { ...source, businessName: source?.businessName ?? '', id: this.selectedLead, programCode: this.programCode, isShowOnDirectory: isShowOnDirectory, isProvidingServices: isProvidingServices, mcoOrgId: source?.regionalCenter };

    if (this.entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
      payload = { ...payload, providerType: ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[this.enrollmentType] }
    }

    return payload;
  }

  parseDate(dateStr: string) {
    const [datePart, timePart] = dateStr.split(" ");
    const [h, m, s] = timePart.split(":").slice(0, 3);
    return new Date(`${datePart} ${h}:${m}:${s}`);
  }
}
